import React from 'react';
import SankeyNode from './SankeyNode';
import SankeyLink from './SankeyLink';
import { sankeyChartColors } from '../utils/analysisColorConfig';
import { IDimensions, IMask, ISankeyLink, ISankeyNode } from './interfaces';
import { NODE_HORIZONTAL_SPACE, NODE_TOOLTIP_HEIGHT } from './constants';
import { useCurrentUrl } from '../../../utils/routing';
import { useFullscreenContext } from '../../../utils/contexts/FullscreenContext';
import { ChartMode } from '../types';

interface IProps {
  isBase: boolean;
  links: ISankeyLink[];
  nodes: ISankeyNode[];
  onNodeMouseOver: (index: number) => void;
  onNodeMouseLeave: (index: number) => void;
  onResetVisibleStatus: () => void;
  onLinkMouseOver: (
    ev: React.MouseEvent<SVGElement, MouseEvent>,
    linkIndex: number,
  ) => void;
  onNodeClick: (
    ev: React.MouseEvent<SVGElement, MouseEvent>,
    nodeIndex: number,
  ) => void;
  onLinkMouseLeave: () => void;
  onLinkMouseMove: (
    ev: React.MouseEvent<SVGElement, MouseEvent>,
    linkIndex?: number,
  ) => void;
  isObserveMode: boolean;
  dimensions: IDimensions;
  mode: ChartMode;
}

export default ({
  isBase,
  links,
  nodes,
  onNodeMouseOver,
  onNodeMouseLeave,
  onNodeClick,
  onResetVisibleStatus,
  onLinkMouseOver,
  onLinkMouseLeave,
  onLinkMouseMove,
  isObserveMode,
  dimensions,
  mode,
}: IProps) => {
  const { isFullScreen } = useFullscreenContext();
  const { isReportInUrl } = useCurrentUrl();

  const notResponsive =
    isFullScreen || (isReportInUrl && mode !== ChartMode.Compact);

  const width = dimensions.width + NODE_HORIZONTAL_SPACE;

  // avoid small vertical mov;ement when you modify the chart
  const height =
    (Math.floor(dimensions.height / NODE_TOOLTIP_HEIGHT) + 1) *
    NODE_TOOLTIP_HEIGHT;

  return (
    <svg
      onClick={onResetVisibleStatus}
      viewBox={`0 0 ${width} ${height}`}
      width={notResponsive ? width : '100%'}
      height={notResponsive ? height : '100%'}
    >
      <defs>
        <pattern
          id="pattern-stripe"
          width="4"
          height="4"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width="1" height="4" fill="white" />
        </pattern>
        <mask id={IMask.MASK_STRIPE_ID}>
          <rect
            x="0"
            y="0"
            width="10000"
            height="10000"
            fill="url(#pattern-stripe)"
          />
        </mask>
        <pattern
          id="pattern-checkered"
          width="4"
          height="4"
          patternUnits="userSpaceOnUse"
          stroke="white"
          strokeLinecap="square"
          strokeWidth="3"
          patternTransform="rotate(45)"
        >
          <line x1="0" y1="0" x2="0" y2="4"></line>
          <line x1="0" y1="0" x2="4" y2="0"></line>
        </pattern>
        <mask id={IMask.MASK_ALT_ID}>
          <rect
            x="0"
            y="0"
            width="10000"
            height="10000"
            fill="url(#pattern-checkered)"
          />
        </mask>
      </defs>
      <g>
        {links.map((link) => (
          <SankeyLink
            key={link.index}
            index={link.index}
            link={link}
            color={
              sankeyChartColors[link.colorIndex % sankeyChartColors.length]
            }
            onMouseOver={onLinkMouseOver}
            onMouseMove={onLinkMouseMove}
            onMouseLeave={onLinkMouseLeave}
          />
        ))}
      </g>
      <g>
        {nodes.map((node) => (
          <SankeyNode
            key={node.id}
            color={
              sankeyChartColors[node.colorIndex % sankeyChartColors.length]
            }
            node={node}
            isBase={isBase}
            isObserveMode={isObserveMode}
            onMouseOver={onNodeMouseOver}
            onMouseLeave={onNodeMouseLeave}
            onNodeClick={onNodeClick}
            mode={mode}
          />
        ))}
      </g>
    </svg>
  );
};
